import { useSdkContext } from "@product/scmp-sdk";

import { RouteType } from "./enum";

export type GroupSection = {
  key: string;
  sections: Section[];
};

export type SitemapItemLink = {
  isExternal?: boolean;
  isNewTag?: boolean;
  label: string;
  url: {
    alias: string;
    applicationId?: string;
    entityId?: string;
    entityUuid?: string;
    type?: RouteType;
  };
};

type Section = {
  label: string;
  links: SitemapItemLink[];
};

type Sitemap = {
  sections: (GroupSection | Section)[];
};

export const useSitemap = () => {
  const { config } = useSdkContext();

  const sitemap: Sitemap = {
    sections: [
      {
        label: "Products & Services",
        links: [
          {
            isExternal: true,
            label: "Digital Subscription",
            url: {
              alias: config.subscribe.host,
            },
          },
          {
            isExternal: true,
            label: "Bundle Subscription",
            url: {
              alias: `${config.subscribe.host}/bundle`,
            },
          },

          {
            isExternal: true,
            label: "Group Subscriptions",
            url: {
              alias: `${config.subscribe.host}/group`,
              applicationId: "group-subscriptions",
            },
          },
          {
            label: "Mobile Editions",
            url: {
              alias: "/mobile",
            },
          },
          {
            isExternal: true,
            label: "ePaper",
            url: {
              alias: "/epaper",
            },
          },
          {
            label: "RSS Feeds",
            url: {
              alias: "/rss",
            },
          },
          {
            label: "Newsletters",
            url: {
              alias: "/newsletters",
            },
          },
          {
            isExternal: true,
            label: "Buy SCMP Photos",
            url: {
              alias: "https://photo.scmp.com/",
              applicationId: "photo",
            },
          },
          {
            isExternal: true,
            isNewTag: true,
            label: "Syndication & Licensing",
            url: {
              alias: "https://enterprise.scmp.com/licensing",
            },
          },
        ],
      },
      {
        label: "About Us",
        links: [
          {
            label: "All Topics",
            url: {
              alias: "/topics",
            },
          },
          {
            label: "All Authors",
            url: {
              alias: "/authors",
            },
          },
          {
            isExternal: true,
            label: "SCMP Publishers",
            url: {
              alias: "https://corp.scmp.com/",
              applicationId: "scmp-publishers",
            },
          },
          {
            label: "Policies and Standards",
            url: {
              alias: "/policies-and-standards",
              entityId: "3074375",
              entityUuid: "dd1bc258-b7b9-437c-bc4c-99130d8a5a3f",
              type: RouteType.Page,
            },
          },
          {
            label: "Privacy Policy",
            url: {
              alias: "/privacy-policy",
              entityId: "800172",
              entityUuid: "d1c18b93-cb6c-48a2-bf84-7c2acb96d4cc",
              type: RouteType.Page,
            },
          },
          {
            label: "Terms & Conditions",
            url: {
              alias: "/terms-conditions",
              entityId: "800173",
              entityUuid: "c18b91b9-6e24-45dc-ba05-974848666e4b",
              type: RouteType.Page,
            },
          },
          {
            label: "Commenting Guidelines",
            url: {
              alias: "/Commenting-Submission-Guidelines",
              entityId: "3076709",
              entityUuid: "3460b664-d04c-44d0-83db-cec47ac0152c",
              type: RouteType.Page,
            },
          },
          {
            isExternal: true,
            label: "Help Centre",
            url: {
              alias: "https://help.scmp.com/hc",
              applicationId: "help-centre",
            },
          },
          {
            isExternal: true,
            label: "Career with SCMP",
            url: {
              alias: "https://corp.scmp.com/work-for-us/",
              applicationId: "work-for-us",
            },
          },
          {
            label: "Contact Us",
            url: {
              alias: "/contact-us",
              entityId: "800171",
              entityUuid: "b580e1d5-6698-42df-a1c2-1f7bdb2a0a04",
              type: RouteType.Page,
            },
          },
        ],
      },
      {
        label: "Our Sites",
        links: [
          {
            isNewTag: true,
            label: "Young Post",
            url: {
              alias: "/yp",
              applicationId: "yp",
            },
          },

          {
            isNewTag: true,
            label: "Posties",
            url: {
              alias: "/posties",
              applicationId: "posties",
            },
          },
          {
            isExternal: true,
            label: "Serviced Apartments",
            url: {
              alias: "http://www.ays.com.hk/",
              applicationId: "serviced-apartments",
            },
          },
          {
            isNewTag: true,
            label: "SCMP Cooking",
            url: {
              alias: "/cooking",
              applicationId: "scmp-cooking",
            },
          },
          {
            isExternal: true,
            label: "Cosmopolitan",
            url: {
              alias: "https://www.cosmopolitan.com.hk",
              applicationId: "cosmopolitan",
            },
          },
          {
            isExternal: true,
            label: "ELLE",
            url: {
              alias: "https://www.elle.com.hk",
              applicationId: "elle",
            },
          },
          {
            isExternal: true,
            label: "Esquire",
            url: {
              alias: "https://www.esquirehk.com",
              applicationId: "esquire",
            },
          },
          {
            isExternal: true,
            label: "ELLE MEN",
            url: {
              alias: "https://www.ellemen.com.hk/ ",
              applicationId: "elle-men",
            },
          },
          {
            isExternal: true,
            label: "Harper's BAZAAR",
            url: {
              alias: "https://www.harpersbazaar.com.hk",
              applicationId: "harpers-bazaar",
            },
          },
          {
            isExternal: true,
            label: "CosMart" /*  Cspell:ignore Cosmart */,
            url: {
              alias: "https://www.cosmart.hk",
              applicationId: "cosmart",
            },
          },
        ],
      },
      {
        key: "SCMP Labs & Other",
        sections: [
          {
            label: "Other",
            links: [
              {
                isExternal: true,
                label: "Advertising+",
                url: {
                  alias: "http://advertising.scmp.com/",
                  applicationId: "advertising-with-us",
                },
              },
              {
                isExternal: true,
                label: "SCMP Live",
                url: {
                  alias: "https://live.scmp.com",
                  applicationId: "scmp-events",
                },
              },
              {
                isExternal: true,
                label: "Enterprise+",
                url: {
                  alias: "https://enterprise.scmp.com/",
                  applicationId: "enterprise",
                },
              },
              {
                isExternal: true,
                label: "Learn",
                url: {
                  alias: "https://www.scmplearn.com/",
                  applicationId: "scmp-learn",
                },
              },
              {
                label: "SCMP PR Blog",
                url: {
                  alias: "/topics/scmp-pr-blog",
                  entityId: "512674",
                  entityUuid: "b39eda92-a5a8-443c-8ebf-0d3989acb0f3",
                  type: RouteType.Topic,
                },
              },
            ],
          },
        ],
      },
    ],
  };

  return { sitemap };
};
